import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { DEFAULT_LANGUAGE, LOCALE_STORAGE_KEY } from './languages'

// Import English locale files
import enCommon from '@/locales/en/common.json'
import enAuth from '@/locales/en/auth.json'
import enProfile from '@/locales/en/profile.json'
import enProjects from '@/locales/en/projects.json'
import enConversations from '@/locales/en/conversations.json'
import enOrganizations from '@/locales/en/organizations.json'
import enAssets from '@/locales/en/assets.json'
import enAdmin from '@/locales/en/admin.json'
import enMobile from '@/locales/en/mobile.json'
import enFde from '@/locales/en/fde.json'

// Import Russian locale files
import ruCommon from '@/locales/ru/common.json'
import ruAuth from '@/locales/ru/auth.json'
import ruProfile from '@/locales/ru/profile.json'
import ruProjects from '@/locales/ru/projects.json'
import ruConversations from '@/locales/ru/conversations.json'
import ruOrganizations from '@/locales/ru/organizations.json'
import ruAssets from '@/locales/ru/assets.json'
import ruAdmin from '@/locales/ru/admin.json'
import ruMobile from '@/locales/ru/mobile.json'
import ruFde from '@/locales/ru/fde.json'

// Import Spanish locale files
import esCommon from '@/locales/es/common.json'
import esAuth from '@/locales/es/auth.json'
import esProfile from '@/locales/es/profile.json'
import esProjects from '@/locales/es/projects.json'
import esConversations from '@/locales/es/conversations.json'
import esOrganizations from '@/locales/es/organizations.json'
import esAssets from '@/locales/es/assets.json'
import esAdmin from '@/locales/es/admin.json'
import esMobile from '@/locales/es/mobile.json'
import esFde from '@/locales/es/fde.json'

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    profile: enProfile,
    projects: enProjects,
    conversations: enConversations,
    organizations: enOrganizations,
    assets: enAssets,
    admin: enAdmin,
    mobile: enMobile,
    fde: enFde,
  },
  ru: {
    common: ruCommon,
    auth: ruAuth,
    profile: ruProfile,
    projects: ruProjects,
    conversations: ruConversations,
    organizations: ruOrganizations,
    assets: ruAssets,
    admin: ruAdmin,
    mobile: ruMobile,
    fde: ruFde,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    profile: esProfile,
    projects: esProjects,
    conversations: esConversations,
    organizations: esOrganizations,
    assets: esAssets,
    admin: esAdmin,
    mobile: esMobile,
    fde: esFde,
  },
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: 'common',
    ns: ['common', 'auth', 'profile', 'projects', 'conversations', 'organizations', 'assets', 'admin', 'mobile', 'fde'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  })

// Sync language changes to backend
i18n.on('languageChanged', (lng: string) => {
  // Extract base language code (e.g., 'en-US' -> 'en')
  const locale = lng.split('-')[0]

  // Get CSRF token from meta tag
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

  fetch('/profile/locale', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ locale }),
  }).catch((error) => {
    console.error('Failed to sync locale to backend:', error)
  })
})

/**
 * Get the base language code from i18n (e.g., 'en-US' -> 'en')
 * Use this instead of manually splitting i18n.language
 */
export function getBaseLanguage(): string {
  return i18n.language?.split('-')[0] || 'en'
}

/**
 * Maps i18n language codes to browser locale codes for date/number formatting
 */
const DATE_LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  es: 'es-ES',
}

/**
 * Get the browser locale code for date/number formatting based on current i18n language
 */
export function getDateLocale(): string {
  return DATE_LOCALE_MAP[getBaseLanguage()] || 'en-US'
}

// Import date-fns locales for use in components
import { enUS, ru, es } from 'date-fns/locale'
import type { Locale } from 'date-fns'

/**
 * Maps i18n language codes to date-fns Locale objects
 */
const DATE_FNS_LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  ru: ru,
  es: es,
}

/**
 * Get date-fns Locale object for the current language
 * Use this for date-fns formatting functions
 */
export function getDateFnsLocale(): Locale {
  return DATE_FNS_LOCALE_MAP[getBaseLanguage()] || enUS
}

/**
 * Languages that use 24-hour time format
 */
export const LANGUAGES_24H_FORMAT = new Set(['ru'])

/**
 * Check if current language uses 24-hour time format
 */
export function uses24HourFormat(): boolean {
  return LANGUAGES_24H_FORMAT.has(getBaseLanguage())
}

export default i18n
